/**
 * engine-data.js — headless catalogue for the Studio patch matrix.
 *
 * Everything the matrix needs to describe a voice without touching the DOM:
 * ordering, parameter tables, naming, the per-voice hue ramp, the
 * TONE / CHARACTER / SHAPE·OUT column grouping, and the smart-randomisation
 * rules. Rendering components import from here; nothing here imports a
 * component.
 */

import {
  ENGINE_PRIMARY_PARAMS,
  ENGINE_EXPANDED_PARAMS,
  ENGINE_DISPLAY_NAMES,
  ENGINE_SHORT_NAMES,
} from './components/engine-params.js';

export {
  ENGINE_PRIMARY_PARAMS,
  ENGINE_EXPANDED_PARAMS,
  ENGINE_DISPLAY_NAMES,
  ENGINE_SHORT_NAMES,
};

/** Voice order — one matrix row each, low frequency content first. */
export const ENGINE_TYPES = [
  'analog_kick',
  'membrane',
  'hybrid_snare',
  'clap',
  'closed_hat',
  'open_hat',
  'cymbal',
  'tom',
  'rim',
  'shaker',
  'impact',
  'fm',
];

/**
 * Strudel sound name per engine, used by the code export.
 *
 * Closed and open hat are one engine: `registerSound` publishes `dsonic_hhat`
 * (whose default template is `closed`) plus one `dsonic_hhat_<template>` per
 * template. `dsonic_ohat` — what this table used to name — was never
 * registered, so exported open-hat lines silently did nothing.
 */
export const ENGINE_SOUND_NAMES = {
  analog_kick: 'dsonic_akick',
  membrane: 'dsonic_membrane',
  hybrid_snare: 'dsonic_hsnare',
  clap: 'dsonic_hclap',
  closed_hat: 'dsonic_hhat',
  open_hat: 'dsonic_hhat_open',
  cymbal: 'dsonic_hcymbal',
  tom: 'dsonic_htom',
  rim: 'dsonic_hrim',
  shaker: 'dsonic_shaker',
  fold: 'dsonic_fold',
  impact: 'dsonic_impact',
  fm: 'dsonic_fmperc',
};

/**
 * Voice hue ramp. A single spectral walk from ember (kick) to magenta (FM),
 * passing through the DSonic spring-green at open hat so the brand accent is
 * a member of the family rather than a stranger sitting on top of it. Each
 * hue is carried by that voice's tick, name, parameter bars, level meter and
 * sequencer lane — the one thread that ties the two halves of the row
 * together.
 */
export const VOICE_HUES = {
  analog_kick: '#f0655e',
  membrane: '#f28a3c',
  hybrid_snare: '#f5b13d',
  clap: '#d9d24f',
  closed_hat: '#8fd44f',
  open_hat: '#35e08d',
  cymbal: '#2fd4c0',
  tom: '#3ab3e0',
  rim: '#5b95ef',
  shaker: '#8a80f2',
  impact: '#b96ef0',
  fm: '#ee6cba',
  fold: '#ff8787',
};

/** Column-group headings above the parameter half of the matrix. */
export const PARAM_GROUPS = ['TONE', 'CHARACTER', 'SHAPE · OUT'];

/**
 * Which of the three column groups a parameter belongs to.
 *
 * Every engine's primary list is already ordered source → character →
 * shaping/output, so an even split into thirds lands on the semantic
 * boundaries (9 params → 3/3/3, clap's 6 → 2/2/2).
 */
export function paramGroupIndex(index, total) {
  return Math.min(2, Math.floor((index * 3) / total));
}

/** Primary params for an engine, each tagged with its column group. */
export function getGroupedParams(engineType) {
  const params = ENGINE_PRIMARY_PARAMS[engineType] || [];
  return params.map((param, i) => ({
    ...param,
    group: paramGroupIndex(i, params.length),
    isGroupEnd:
      i === params.length - 1 ||
      paramGroupIndex(i + 1, params.length) !== paramGroupIndex(i, params.length),
  }));
}

/* ------------------------------------------------------------------ *
 * Type parameters
 *
 * A handful of params are enumerations on the engine side but plain
 * numbers in the UI. The matrix stores the numeric index and converts on
 * the way out.
 * ------------------------------------------------------------------ */

export const IMPACT_TYPE_MAPPINGS = {
  transientType: ['impulse', 'click', 'brush', 'mallet', 'noise-burst'],
  bodyType: ['sine', 'triangle', 'noise', 'filtered-noise', 'resonant'],
  tailType: ['noise-tail', 'reverb-like', 'shimmer', 'damped'],
};

export const CLAP_TYPE_MAPPINGS = {
  strikeType: ['palm', 'fingers', 'cupped'],
  driveType: ['soft', 'hard', 'tape'],
};

function mappingsFor(engineType) {
  if (engineType === 'impact') return IMPACT_TYPE_MAPPINGS;
  if (engineType === 'clap') return CLAP_TYPE_MAPPINGS;
  return null;
}

/** Engine-facing value (string enum where applicable) → UI numeric index. */
export function toUiValue(engineType, paramName, value) {
  const mapping = mappingsFor(engineType)?.[paramName];
  if (!mapping || typeof value !== 'string') return value;
  const index = mapping.indexOf(value);
  return index >= 0 ? index : 0;
}

/** UI numeric index → engine-facing value (string enum where applicable). */
export function toEngineValue(engineType, paramName, value) {
  const mapping = mappingsFor(engineType)?.[paramName];
  if (!mapping || typeof value !== 'number') return value;
  return mapping[Math.round(value)] || mapping[0];
}

/* ------------------------------------------------------------------ *
 * Smart randomisation
 *
 * Unconstrained randomisation reliably produces silence (zero decay, zero
 * noise) or mush (runaway feedback). These bounds are expressed in
 * normalised 0–1 range space and keep every roll audible.
 * ------------------------------------------------------------------ */

const SMART_RANDOM_CONSTRAINTS = {
  _global: {
    decay: { minSafe: 0.3, weighted: true },
    bodyDecay: { minSafe: 0.25, weighted: true },
    pitchEnvDecay: { minSafe: 0.2, weighted: true },
    modIndexDecay: { minSafe: 0.25, weighted: true },
    pitchRampDecay: { minSafe: 0.25, weighted: true },
    attackLength: { minSafe: 0.2, weighted: true },

    metalAmt: { minSafe: 0.4, weighted: false },
    toneAmt: { minSafe: 0.2, weighted: true },
    noiseLevel: { minSafe: 0, maxSafe: 0.6, weighted: true },

    driveAmt: { minSafe: 0, maxSafe: 0.7, weighted: true },

    hpfCutoff: { minSafe: 0, maxSafe: 0.7, weighted: true },
    lpfCutoff: { minSafe: 0.4, maxSafe: 1, weighted: true },

    compAmt: { minSafe: 0, maxSafe: 0.7, weighted: true },
    feedbackAmt: { minSafe: 0, maxSafe: 0.7, weighted: true },
  },

  analog_kick: {
    bodyDecay: { minSafe: 0.3, weighted: true },
    clickLevel: { minSafe: 0.15, weighted: true },
    pitchEnvAmt: { minSafe: 0.2, maxSafe: 0.85, weighted: true },
  },
  membrane: {
    coupling12: { minSafe: 0.15, maxSafe: 0.85, weighted: true },
    wireLevel: { minSafe: 0.2, weighted: true },
    decay: { minSafe: 0.35, weighted: true },
  },
  hybrid_snare: {
    noiseLevel: { minSafe: 0.35, weighted: false },
    wireLevel: { minSafe: 0.2, weighted: true },
    bumpAmt: { minSafe: 0.2, weighted: true },
    bodyDecay: { minSafe: 0.35, weighted: true },
  },
  clap: {
    noiseLevel: { minSafe: 0.5, weighted: false },
    transientLevel: { minSafe: 0.3, weighted: true },
    decay: { minSafe: 0.25, weighted: true },
  },
  closed_hat: {
    oscTune: { minSafe: 0.3, maxSafe: 0.8, weighted: true },
    hiFilterLevel: { minSafe: 0.3, weighted: false },
    loFilterLevel: { minSafe: 0.3, weighted: false },
    decay: { minSafe: 0.25, weighted: true },
    hpfCutoff: { minSafe: 0, maxSafe: 0.6, weighted: true },
  },
  open_hat: {
    oscTune: { minSafe: 0.3, maxSafe: 0.8, weighted: true },
    hiFilterLevel: { minSafe: 0.3, weighted: false },
    loFilterLevel: { minSafe: 0.3, weighted: false },
    decay: { minSafe: 0.35, weighted: true },
    hpfCutoff: { minSafe: 0, maxSafe: 0.65, weighted: true },
  },
  cymbal: {
    richness: { minSafe: 0.3, weighted: true },
    decay: { minSafe: 0.3, weighted: true },
  },
  tom: {
    overtoneAmt: { minSafe: 0.2, weighted: true },
    decay: { minSafe: 0.3, weighted: true },
  },
  rim: {
    enhance: { minSafe: 0.2, weighted: true },
    decay: { minSafe: 0.25, weighted: true },
  },
  shaker: {
    grainDensity: { minSafe: 0.25, maxSafe: 0.85, weighted: true },
    decay: { minSafe: 0.3, weighted: true },
  },
  fold: {
    foldAmt: { minSafe: 0.2, weighted: true },
    decay: { minSafe: 0.3, weighted: true },
  },
  impact: {
    transientLevel: { minSafe: 0.3, weighted: true },
    bodyDecay: { minSafe: 0.2, weighted: true },
    tailDecay: { minSafe: 0.3, weighted: true },
    overallDecay: { minSafe: 0.25, weighted: true },
  },
  fm: {
    modIndex: { minSafe: 0.15, maxSafe: 0.75, weighted: true },
    brightness: { minSafe: 0.2, weighted: true },
    decay: { minSafe: 0.3, weighted: true },
  },
};

function getParamConstraint(engineType, paramName) {
  return (
    SMART_RANDOM_CONSTRAINTS[engineType]?.[paramName] ||
    SMART_RANDOM_CONSTRAINTS._global[paramName] ||
    null
  );
}

/** Triangle distribution centred on 0.5 — extremes stay possible but rare. */
function weightedRandom() {
  return (Math.random() + Math.random()) / 2;
}

/** Every parameter an engine exposes: primary list plus expanded sections. */
export function getAllParams(engineType) {
  const seen = new Set();
  const all = [];

  (ENGINE_PRIMARY_PARAMS[engineType] || []).forEach((param) => {
    seen.add(param.name);
    all.push(param);
  });

  Object.values(ENGINE_EXPANDED_PARAMS[engineType] || {}).forEach((section) => {
    section.forEach((param) => {
      if (!seen.has(param.name)) {
        seen.add(param.name);
        all.push(param);
      }
    });
  });

  return all;
}

/** Default value for every parameter of an engine, primary and expanded. */
export function getAllDefaults(engineType) {
  const defaults = {};
  getAllParams(engineType).forEach((param) => {
    defaults[param.name] = param.default;
  });
  return defaults;
}

/** Default value for just the nine params the matrix puts on screen. */
export function getPrimaryDefaults(engineType) {
  const defaults = {};
  (ENGINE_PRIMARY_PARAMS[engineType] || []).forEach((param) => {
    defaults[param.name] = param.default;
  });
  return defaults;
}

/** A fresh, audible random patch for one engine (primary + expanded params). */
export function randomizeEngine(engineType) {
  const values = {};

  getAllParams(engineType).forEach((param) => {
    const constraint = getParamConstraint(engineType, param.name);
    let normalized;

    if (constraint) {
      const min = constraint.minSafe ?? 0;
      const max = constraint.maxSafe ?? 1;
      const roll = constraint.weighted ? weightedRandom() : Math.random();
      normalized = min + roll * (max - min);
    } else {
      normalized = Math.random();
    }

    values[param.name] = param.min + normalized * (param.max - param.min);
  });

  return values;
}

/* ------------------------------------------------------------------ *
 * Value formatting
 * ------------------------------------------------------------------ */

/** Position of a value within its parameter range, clamped to 0–1. */
export function normalize(param, value) {
  const span = param.max - param.min;
  if (!span) return 0;
  return Math.min(1, Math.max(0, (value - param.min) / span));
}

/** Map a 0–1 position back onto a parameter's range. */
export function denormalize(param, normalized) {
  return param.min + Math.min(1, Math.max(0, normalized)) * (param.max - param.min);
}

/**
 * Split a value into the digits and the dim unit suffix the matrix renders
 * separately, so units read as annotation rather than as part of the number.
 */
export function formatValue(param, value) {
  const decimals = param.decimals ?? 2;
  let text = Number(value).toFixed(decimals);

  // Frequencies above a kilohertz get a k suffix so the cell never wraps.
  if (param.unit === 'Hz' && Math.abs(value) >= 1000) {
    return { text: (value / 1000).toFixed(value >= 10000 ? 1 : 2), unit: 'k' };
  }

  // Trim a trailing ".00" — a plain integer reads faster than padded zeros.
  if (decimals > 0 && text.endsWith('.' + '0'.repeat(decimals))) {
    text = text.slice(0, -(decimals + 1));
  }

  return { text, unit: param.unit || '' };
}

/** Linear gain (0–1) as a mixer-style dB readout. */
export function gainToDb(gain) {
  if (gain <= 0.001) return '-∞';
  const db = 20 * Math.log10(gain);
  if (db >= 0) return `+${db.toFixed(1)}`;
  return db.toFixed(1);
}
